/**
 * The server-supplied message on an `ApiError`, if any (diagnostic/validation
 * detail), scrubbed for the white-label brand (DNKADM-0044).
 *
 * Previously copy-pasted into each `*Error.ts` mapper; extracted here so every
 * forwarded server message is read — and brand-scrubbed — in one place. The
 * `scrubBrand` pass turns a stray backend "GoHighLevel"/"GHL" into "Journix"
 * at the render boundary, since these messages are surfaced to staff verbatim.
 */
import { ApiError } from '@/api/errors';
import { v2ErrorCode } from '@/api/v2Errors';
import {
  declineReasonLineFromError,
  isProviderMessage,
  knownVaultSentenceLine,
} from '@/utils/declineReason';
import { REFUND_IN_PROGRESS_CODE, REFUND_IN_PROGRESS_MESSAGE } from '@/utils/refundInProgress';
import { scrubBrand } from '@/utils/whitelabel';

export const LOCATION_ACCESS_FORBIDDEN_MESSAGE = "You don't have access to this location.";

/**
 * Return the `ApiError`'s server-supplied `details.message` (brand-scrubbed),
 * or `null` when the error is not an `ApiError` or carries no string message.
 *
 * `REFUND_IN_PROGRESS` (409, DNKAPI-0651 item 5 / DNKADM-0452) reads the short guard line
 * instead of dink-api’s long sentence; every refund error surface goes through this seam.
 *
 * Provider text never passes (fix-plain-card-errors). dink-api passes a pay-api fault through
 * as-is, so `details.message` can be "Square rejected the card step: CARD_DECLINED" or a bare
 * Square code. Such a message reads as its plain decline line when the code maps to one, and
 * otherwise as null, so the caller shows its own plain copy. dink's own sentences, which may
 * name Square ("Check the payment in Square"), are not provider text and pass as before.
 * pay-api's own actionable vault sentences ("this card is already on file for another
 * payer") read their short plain line (`knownVaultSentenceLine`).
 */
export function serverMessage(error: unknown): string | null {
  if (error instanceof ApiError && v2ErrorCode(error) === REFUND_IN_PROGRESS_CODE) {
    return REFUND_IN_PROGRESS_MESSAGE;
  }
  if (error instanceof ApiError && typeof error.details?.['message'] === 'string') {
    const message = error.details['message'] as string;
    const vaultLine = knownVaultSentenceLine(message);
    if (vaultLine !== null) return declineReasonLineFromError(error) ?? vaultLine;
    if (isProviderMessage(message)) return declineReasonLineFromError(error);
    return scrubBrand(message);
  }
  return null;
}

/**
 * Shared mapper for location-scoped admin calls. A location-access `403` gets the
 * fixed staff-facing line; other errors fall through to the ordinary server
 * message seam.
 */
export function locationScopedErrorMessage(error: unknown): string | null {
  if (error instanceof ApiError && error.code === 'FORBIDDEN') {
    return LOCATION_ACCESS_FORBIDDEN_MESSAGE;
  }
  return serverMessage(error);
}
